import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LevelAbs, LevelCnf } from 'src/app/app-config';
import { BeControleFinanca } from '@app/models/BeControleFinanca';
import { BeVeiculo } from '@app/models/BeVeiculo';
import { AuthService } from '@app/services/auth.service';
import { SnackBarService } from '@app/services/snack-bar.service';
import { TokenService } from '@app/services/token.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { NavRoute } from '@app/models/nav.route';
import { ConfirmDialogComponent } from '@app/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';

interface MesInfo {
  vlEntrada: number;
  vlSaida: number;
  vlSaldo: number;
  mes: string;
  ano: number;
  idControleFinanca?: number;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit, OnDestroy {
  public routes$ = new BehaviorSubject<readonly NavRoute[] | null>(null);
  public mesAnterior: MesInfo;
  public mesAtual: MesInfo;
  public mesProximo: MesInfo;

  public subscription?: Subscription;

  public loadingPermissions = true;
  public carregandoFinanceiro = false;
  public carregandoVeiculos = false;

  public get carregando(): boolean {
    return this.loadingPermissions || this.carregandoFinanceiro || this.carregandoVeiculos;
  }

  public hasLevelCnf = false;
  public hasLevelAbs = false;
  public hasAnyTokenRole = false;
  public get hasAnyRole(): boolean {
    return this.hasAnyTokenRole;
  }

  public veiculos: BeVeiculo[] = [];

  public constructor(
    private httpClient: HttpClient,
    private snackBarService: SnackBarService,
    private router: Router,
    private tokenService: TokenService,
    private authService: AuthService,
    private dialog: MatDialog
  ) {
    const dataAtual = new Date();

    const mesAtualNum = dataAtual.getMonth() + 1;
    this.mesAtual = {
      vlEntrada: 0,
      vlSaida: 0,
      vlSaldo: 0,
      mes: ('0' + mesAtualNum).slice(-2),
      ano: dataAtual.getFullYear()
    };

    const dataAnterior = new Date(dataAtual);
    dataAnterior.setMonth(dataAtual.getMonth() - 1);
    this.mesAnterior = {
      vlEntrada: 0,
      vlSaida: 0,
      vlSaldo: 0,
      mes: ('0' + (dataAnterior.getMonth() + 1)).slice(-2),
      ano: dataAnterior.getFullYear()
    };

    const dataProximo = new Date(dataAtual);
    dataProximo.setMonth(dataAtual.getMonth() + 1);
    this.mesProximo = {
      vlEntrada: 0,
      vlSaida: 0,
      vlSaldo: 0,
      mes: ('0' + (dataProximo.getMonth() + 1)).slice(-2),
      ano: dataProximo.getFullYear()
    };
  }

  public ngOnInit(): void {
    this.checkPermissions();
  }

  public ngOnDestroy(): void {
    this.unsubscribe();
  }

  private checkPermissions(): void {
    this.loadingPermissions = true;
    this.tokenService.getTokenUserCredentials().subscribe(tokenStr => {
      if (tokenStr) {
        this.tokenService.decodeToken(tokenStr).subscribe(token => {
          if (token) {
            this.hasAnyTokenRole = token.roles.length > 0;
            this.hasLevelCnf = AuthService.canTokenActivate(token, LevelCnf);
            this.hasLevelAbs = AuthService.canTokenActivate(token, LevelAbs);

            if (this.hasLevelCnf) {
              this.buscarDadosFinanceiros();
            }

            if (this.hasLevelAbs) {
              this.buscarVeiculos();
            }
          }
          this.loadingPermissions = false;
          this.checkRedirect();
        });
      } else {
        this.loadingPermissions = false;
        this.checkRedirect();
      }
    });
  }

  private checkRedirect(): void {
    if (!this.hasAnyRole) {
      this.routes$.next(null);
      this.authService.logout();
      this.snackBarService.exibirErro('Você não possui permissão para acessar o sistema.');
    }
  }

  public buscarVeiculos(): void {
    this.carregandoVeiculos = true;
    this.httpClient.get<BeVeiculo[]>('/api/Veiculos').subscribe({
      next: (res) => {
        this.veiculos = res;
      },
      error: (err) => {
        console.error(err);
        this.snackBarService.exibirErro("Erro ao buscar veículos.");
      },
      complete: () => {
        this.carregandoVeiculos = false;
      }
    });
  }

  public buscarDadosFinanceiros(): void {
    this.carregandoFinanceiro = true;
    this.subscription = this.httpClient.get<BeControleFinanca[][]>(
      '/api/ControleFinancas/ListarMesAnteriorAtualProximo'
    ).subscribe({
      next: (res) => {
        this.processarDadosFinanceiros(res);
      },
      error: (erro) => {
        this.tratarErroConsulta(erro);
      },
      complete: () => {
        this.carregandoFinanceiro = false;
        this.unsubscribe();
      }
    });
  }
  private processarDadosFinanceiros(dados: BeControleFinanca[][]): void {
    if (!dados || dados.length < 3) {
      this.snackBarService.exibirErro("Não foram encontrados valores para os meses solicitados.");
      this.limparValores();
      return;
    }

    if (dados[1]?.length > 0) {
      this.mesAnterior.vlEntrada = dados[1][0].vlEntrada ?? 0;
      this.mesAnterior.vlSaida = dados[1][0].vlSaida ?? 0;
      this.mesAnterior.vlSaldo = this.mesAnterior.vlEntrada - this.mesAnterior.vlSaida;
      this.mesAnterior.idControleFinanca = dados[1][0].idControleFinanca;
    }

    if (dados[0]?.length > 0) {
      this.mesAtual.vlEntrada = dados[0][0].vlEntrada ?? 0;
      this.mesAtual.vlSaida = dados[0][0].vlSaida ?? 0;
      this.mesAtual.vlSaldo = this.mesAtual.vlEntrada - this.mesAtual.vlSaida;
      this.mesAtual.idControleFinanca = dados[0][0].idControleFinanca;
    }

    if (dados[2]?.length > 0) {
      this.mesProximo.vlEntrada = dados[2][0].vlEntrada ?? 0;
      this.mesProximo.vlSaida = dados[2][0].vlSaida ?? 0;
      this.mesProximo.vlSaldo = this.mesProximo.vlEntrada - this.mesProximo.vlSaida;
      this.mesProximo.idControleFinanca = dados[2][0].idControleFinanca;
    }
  }

  private tratarErroConsulta(erro: any): void {
    console.error('Erro ao buscar dados financeiros:', erro);
    this.limparValores();
    this.snackBarService.exibirErro("Erro ao consultar valores financeiros.");
    this.carregandoFinanceiro = false;
    this.unsubscribe();
  }

  private unsubscribe(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }

  private limparValores(): void {
    this.mesAnterior.vlEntrada = 0;
    this.mesAnterior.vlSaida = 0;
    this.mesAnterior.vlSaldo = 0;

    this.mesAtual.vlEntrada = 0;
    this.mesAtual.vlSaida = 0;
    this.mesAtual.vlSaldo = 0;

    this.mesProximo.vlEntrada = 0;
    this.mesProximo.vlSaida = 0;
    this.mesProximo.vlSaldo = 0;
  }

  public navegarParaControleFinanca(mes: MesInfo): void {
    if (mes.idControleFinanca) {
      this.router.navigate(['/controle-financa', mes.idControleFinanca]);
    } else {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Criar Controle Financeiro',
          message: `Deseja criar um novo registro para o mês ${mes.mes}/${mes.ano}?`
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.onCriarControleFinanca(mes);
        }
      });
    }
  }

  private onCriarControleFinanca(mes: MesInfo): void {
    const novoControle: Partial<BeControleFinanca> = {
      dtControle: new Date(mes.ano, parseInt(mes.mes) - 1, 1)
    };

    this.carregandoFinanceiro = true;
    this.subscription = this.httpClient.post<BeControleFinanca>('/api/ControleFinancas', novoControle).subscribe({
      next: (res) => {
        mes.idControleFinanca = res.idControleFinanca;
        mes.vlEntrada = res.vlEntrada ?? 0;
        mes.vlSaida = res.vlSaida ?? 0;
        mes.vlSaldo = mes.vlEntrada - mes.vlSaida;

        this.snackBarService.exibirSucesso('Controle financeiro criado com sucesso!');
      },
      error: (err) => {
        const msg = err.error?.message || err.message || 'Erro desconhecido';
        this.snackBarService.exibirErro(`Erro ao criar controle financeiro: ${msg}`);
        this.carregandoFinanceiro = false;
        this.unsubscribe();
      },
      complete: () => {
        this.carregandoFinanceiro = false;
        this.unsubscribe();
      }
    });
  }

  public navegarParaAdicionarVeiculo(): void {
    this.router.navigate(['/novo-veiculo']);
  }

  public navegarParaVeiculo(veiculo: BeVeiculo): void {
    if (veiculo.idVeiculo) {
      this.router.navigate(['/veiculo', veiculo.idVeiculo]);
    }
  }

  public onAbastecimento(veiculo: BeVeiculo, event: Event): void {
    event.stopPropagation();
    if (veiculo.idVeiculo) {
      this.router.navigate(['/veiculo', veiculo.idVeiculo], { fragment: 'abastecimentos' });
    }
  }

  public onUsuarios(veiculo: BeVeiculo, event: Event): void {
    event.stopPropagation();
    if (veiculo.idVeiculo) {
      this.router.navigate(['/veiculo', veiculo.idVeiculo], { fragment: 'usuarios' });
    }
  }
}
